import {
  OrderInformation,
  ShippingData,
  Products,
  type OrderInformationRef,
  type ShippingDataRef,
  type ProductsRef,
} from '../display';
import { OrderInformationSkeleton, ProductsSkeleton, ShippingDataSkeleton } from '../skeletons';
import { TenantChildSelector } from '../interaction';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useCreateOrder } from '../../hooks/useCreateOrder';
import type { CreateOrderRequest } from '../../adapters';
import { DuplicateOrderService } from '@shared/services/DuplicateOrderService';
import { useToast } from '@shared/hooks/use-toast';
import { useCurrency } from '../../../shared/hooks/useCurrency';
import { useTenantChildren } from '../../hooks';
import { useAuthStore } from '@shared/stores/authStore';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';

const INITIAL_ORDER_INFO = {
  reference: '',
  store: '',
  paymentMethod: '',
};

const INITIAL_SHIPPING_DATA = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  province: '',
  country: '',
  countryId: 0,
};

export const Main = () => {
  const { t } = useTranslation('createEditOrdersModule');
  const navigate = useNavigate();
  const { createOrder, loading } = useCreateOrder();
  const { toast } = useToast();
  const userCurrency = useCurrency();

  // Referencias a los componentes de formulario
  const orderInformationRef = useRef<OrderInformationRef>(null);
  const shippingDataRef = useRef<ShippingDataRef>(null);
  const productsRef = useRef<ProductsRef>(null);

  // Estado para los datos de los formularios
  const [orderInfoData, setOrderInfoData] = useState(INITIAL_ORDER_INFO);
  const [shippingData, setShippingData] = useState(INITIAL_SHIPPING_DATA);
  const [productsCount, setProductsCount] = useState(0);
  const [formResetKey, setFormResetKey] = useState(0);
  const [selectedTenantChildId, setSelectedTenantChildId] = useState<number | null>(null);

  const tenantChildConfirmRef = useRef<ConfirmModalRef>(null);

  // Track duplicate order data that needs to be preserved through child selection
  const duplicateDataRef = useRef<{
    orderInfo: typeof INITIAL_ORDER_INFO;
    shippingData: typeof INITIAL_SHIPPING_DATA;
    products: any[];
  } | null>(null);
  const hasPendingDuplicateDataRef = useRef(false);

  const parentTeamId = useAuthStore((s) => s.team?.id);

  const {
    isTenant3pl,
    shouldFetchTenantChildren,
    isLoading: tenantChildrenLoading,
    isFetching: tenantChildrenFetching,
  } = useTenantChildren();

  const tenantChildrenIsLoading = shouldFetchTenantChildren && (tenantChildrenLoading || tenantChildrenFetching);

  const shouldShowFormSkeletons = !isTenant3pl && tenantChildrenIsLoading;

  const clientsHttpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const clientsAdapter = useMemo(() => new ClientsApiAdapter(clientsHttpClient), [clientsHttpClient]);

  const isFormLocked = isTenant3pl && !selectedTenantChildId;
  const catalogTenantChildId = isTenant3pl ? (selectedTenantChildId ?? undefined) : undefined;

  const resetFormState = useCallback(() => {
    setOrderInfoData(INITIAL_ORDER_INFO);
    setShippingData(INITIAL_SHIPPING_DATA);
    setProductsCount(0);
    setFormResetKey((prev) => prev + 1);
  }, []);

  const applyDuplicateDataToForm = useCallback(() => {
    if (!duplicateDataRef.current) return;

    const { orderInfo, shippingData, products } = duplicateDataRef.current;

    // Set state
    setOrderInfoData(orderInfo);
    setShippingData(shippingData);

    // Apply to form components with delay for mounting
    setTimeout(() => {
      if (orderInformationRef.current && 'setFormData' in orderInformationRef.current) {
        (orderInformationRef.current as any).setFormData(orderInfo);
      }

      if (shippingDataRef.current && 'setFormData' in shippingDataRef.current) {
        (shippingDataRef.current as any).setFormData(shippingData);
      }

      if (productsRef.current && 'setProducts' in productsRef.current) {
        (productsRef.current as any).setProducts(products);
        setProductsCount(products.length);
      }

      // Clear duplicate data after successful application
      duplicateDataRef.current = null;
      hasPendingDuplicateDataRef.current = false;
    }, 150);
  }, []);

  const applyTenantChildSelection = useCallback(
    (childId: number) => {
      const hadPendingDuplicate = hasPendingDuplicateDataRef.current;

      setSelectedTenantChildId(childId);
      resetFormState();

      // If we had pending duplicate data, re-apply it after reset
      if (hadPendingDuplicate && duplicateDataRef.current) {
        // Apply duplicate data after components re-mount
        setTimeout(() => {
          applyDuplicateDataToForm();
        }, 150);
      }
    },
    [resetFormState, applyDuplicateDataToForm],
  );

  const handleTenantChildSelect = useCallback(
    async (value: string) => {
      const parsedValue = Number(value);
      if (!value || Number.isNaN(parsedValue)) {
        return;
      }

      if (!selectedTenantChildId) {
        applyTenantChildSelection(parsedValue);
        return;
      }

      if (selectedTenantChildId === parsedValue) {
        return;
      }

      let childName = '';
      try {
        const client = await clientsAdapter.getClientById(parsedValue);
        childName = client?.name ?? '';
      } catch {
        childName = '';
      }

      tenantChildConfirmRef.current?.open(
        () => applyTenantChildSelection(parsedValue),
        undefined,
        t('tenantChildSelector.confirm.title'),
        t('tenantChildSelector.confirm.message', {
          childName,
        }),
        t('tenantChildSelector.confirm.confirm'),
        t('tenantChildSelector.confirm.cancel'),
      );
    },
    [applyTenantChildSelection, clientsAdapter, selectedTenantChildId, t],
  );

  // Cargar datos de duplicación al montar el componente
  useEffect(() => {
    const loadDuplicateData = () => {
      const duplicateData = DuplicateOrderService.getDuplicateData();
      if (duplicateData) {
        // Store duplicate data in ref for later application
        duplicateDataRef.current = duplicateData;
        hasPendingDuplicateDataRef.current = true;

        // Show notification
        toast({
          title: t('duplicate.loaded.title'),
          description: t('duplicate.loaded.description'),
          variant: 'default',
        });

        // For non-tenant_3pl users, apply immediately
        if (!isTenant3pl) {
          applyDuplicateDataToForm();
        }
        // For tenant_3pl users, data will be applied after first child selection
      }
    };

    loadDuplicateData();
  }, [t, toast, isTenant3pl, applyDuplicateDataToForm]);

  // Validación del formulario
  const { isFormValid } = useFormValidation(orderInfoData, shippingData, productsCount);

  // Función para crear la orden
  const handleCreateOrder = async () => {
    try {
      if (isTenant3pl && !selectedTenantChildId) {
        toast({
          title: t('tenantChildSelector.requiredTitle'),
          description: t('tenantChildSelector.requiredMessage'),
          variant: 'destructive',
        });
        return;
      }

      // Obtener datos de los formularios
      const orderInfo = orderInformationRef.current?.getFormData();
      const shipping = shippingDataRef.current?.getFormData();

      if (!orderInfo || !shipping) {
        console.error(t('errors.incompleteForm'));
        return;
      }

      // Obtener productos, gastos de envío y descuentos del componente Products
      const products = productsRef.current?.getProducts() || [];
      const shippingCosts = productsRef.current?.getShippingCosts() || 0;
      const discounts = productsRef.current?.getDiscounts() || 0;

      // Validar que haya al menos un producto
      if (products.length === 0) {
        toast({
          title: t('errors.noProducts.title'),
          description: t('errors.noProducts.description'),
          variant: 'destructive',
        });
        return;
      }

      // Construir el payload para la API
      const orderData: CreateOrderRequest = {
        external_id: '0',
        ref: orderInfo.reference,
        payment_method_id: parseInt(orderInfo.paymentMethod) || 1,
        shop_id: parseInt(orderInfo.store) || 1,
        order_created_at: new Date().toISOString(),
        customer: {
          name: shipping.customerName,
          phone: shipping.customerPhone,
          email: shipping.customerEmail,
          address: {
            address_1: shipping.address1,
            ...(shipping.address2 && { address_2: shipping.address2 }),
            zip: shipping.postalCode,
            city: shipping.city,
            province: shipping.province,
            country_code: shipping.country,
          },
        },
        country_id: parseInt(shipping.countryId?.toString() || '1') || 1,
        lines: products.map((product) => ({
          // Si es un pack, usar pack_id. Si es producto, usar product_id
          ...(product.type === 'pack' ? { pack_id: product.pack_id || product.id } : { product_id: product.id }),
          name: product.name,
          quantity: product.quantity,
          amount: product.price,
        })),
        currency: userCurrency,
        total_tax: 0,
        total_shipping: shippingCosts,
        total_discount: discounts,
      };

      // Crear la orden
      await createOrder(orderData);

      // Clear any pending duplicate data
      duplicateDataRef.current = null;
      hasPendingDuplicateDataRef.current = false;
      DuplicateOrderService.clearDuplicateData();
    } catch (error) {
      console.error(t('errors.createOrder'), error);
    }
  };

  // Actualizar datos de los formularios cuando cambien
  useEffect(() => {
    const updateFormData = () => {
      if (orderInformationRef.current) {
        setOrderInfoData(orderInformationRef.current.getFormData());
      }
      if (shippingDataRef.current) {
        setShippingData(shippingDataRef.current.getFormData());
      }
      if (productsRef.current) {
        setProductsCount(productsRef.current.getProductsCount());
      }
    };

    // Actualizar inmediatamente y luego cada 100ms para cambios en tiempo real
    updateFormData();
    const interval = setInterval(updateFormData, 100);

    return () => clearInterval(interval);
  }, []);

  // Cleanup duplicate data on component unmount
  useEffect(() => {
    return () => {
      // If user navigates away without creating order, clear duplicate data
      if (hasPendingDuplicateDataRef.current) {
        duplicateDataRef.current = null;
        hasPendingDuplicateDataRef.current = false;
      }
    };
  }, []);

  // 🍞 Breadcrumbs para la página de crear/editar pedidos
  const breadcrumbs = useMemo(
    () => [{ label: t('breadcrumbs.orders'), path: '/orders' }, { label: t('breadcrumbs.createOrder') }],
    [t],
  );

  // 🍞 Configurar breadcrumbs
  usePageNavigation({ breadcrumbs });

  return (
    <>
      <div className="flex items-center flex-wrap gap-3 absolute right-4 top-2">
        <Button
          variant="outline"
          size="lg"
          className="shrink-0"
          data-testid="cancel-order-button"
          onClick={() => navigate('/orders')}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="shrink-0"
          data-testid="create-order-button"
          onClick={handleCreateOrder}
          disabled={!isFormValid || loading || isFormLocked}
        >
          {loading ? t('buttons.creating') : t('buttons.create')}
        </Button>
      </div>
      <div className="px-2 sm:px-6 pt-4">
        {isTenant3pl && (
          <TenantChildSelector
            parentTeamId={parentTeamId}
            comboboxEnabled={Boolean(isTenant3pl && parentTeamId)}
            selectedTenantChildId={selectedTenantChildId}
            onSelectTenantChild={handleTenantChildSelect}
            isLocked={isFormLocked}
          />
        )}
        {shouldShowFormSkeletons && (
          <>
            <OrderInformationSkeleton />
            <ShippingDataSkeleton />
            <ProductsSkeleton />
          </>
        )}
        {!isFormLocked && !shouldShowFormSkeletons && (
          <>
            <OrderInformation
              key={`order-information-${formResetKey}`}
              ref={orderInformationRef}
              tenantChildId={catalogTenantChildId}
            />
            <ShippingData key={`shipping-data-${formResetKey}`} ref={shippingDataRef} />
            <Products key={`products-${formResetKey}`} ref={productsRef} tenantChildId={catalogTenantChildId} />
          </>
        )}
      </div>
      <ConfirmModal ref={tenantChildConfirmRef} />
    </>
  );
};
